import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { selectPendingPeriodEdit } from 'slices/pricing/rentalFeeDialogSlice'
import { formatDateToBEFormatDateFns } from 'utils/date'

import RentalFeeCalendar from './RentalFeeCalendar'
import RentalFeeDetailsPanel from './RentalFeeDetailsPanel'

import type { RentalPeriodApi } from 'types/api/rental-fee/rentalPeriod'
import type { IsoDateString } from 'types/entities/shared'

// RentalFeeDetailsPanel is still untyped .jsx — remove this cast when it migrates.
const RentalFeeDetailsPanelUntyped = RentalFeeDetailsPanel as React.ElementType

type PendingPeriodEdit = {
  periodId: string
  startDateBE: IsoDateString | null
  endDateBE: IsoDateString | null
}

type RentalFeeFormValues = {
  rentalFeeId: string | null
  activePeriodId: string | null
  startDate: Date | null
  endDate: Date | null
  rentalPeriods: RentalPeriodApi[]
}

type RentalFeesContentViewProps = {
  flexibleHeight?: boolean
}

const RentalFeesContentView = ({ flexibleHeight = false }: RentalFeesContentViewProps) => {
  const theme = useTheme()
  const { watch } = useFormContext<{ rentalFee: RentalFeeFormValues }>()
  const rentalFee = watch('rentalFee')
  const pendingPeriodEdit = useSelector(selectPendingPeriodEdit) as PendingPeriodEdit | null

  const hasRentalFee = Boolean(rentalFee?.rentalFeeId)

  const displayRentalPeriods = useMemo<RentalPeriodApi[]>(() => {
    const periods = rentalFee?.rentalPeriods ?? []
    return periods.map(period => {
      if (rentalFee?.activePeriodId && period.id === rentalFee.activePeriodId) {
        return {
          ...period,
          startDate: rentalFee.startDate ? formatDateToBEFormatDateFns(rentalFee.startDate) : period.startDate,
          endDate: rentalFee.endDate ? formatDateToBEFormatDateFns(rentalFee.endDate) : period.endDate,
        }
      }
      if (pendingPeriodEdit?.periodId === period.id) {
        return {
          ...period,
          startDate: pendingPeriodEdit.startDateBE ?? period.startDate,
          endDate: pendingPeriodEdit.endDateBE ?? period.endDate,
        }
      }
      return period
    })
  }, [rentalFee?.rentalPeriods, rentalFee?.activePeriodId, rentalFee?.startDate, rentalFee?.endDate, pendingPeriodEdit])

  return (
    <Box sx={{ px: 3, opacity: hasRentalFee ? 1 : 0.5, pointerEvents: hasRentalFee ? 'auto' : 'none' }}>
      <Grid
        container
        alignItems="flex-start"
        spacing={2}
        sx={{
          flexDirection: {
            xs: 'column-reverse',
            md: 'row',
            lg: 'column-reverse',
          },
          [theme.breakpoints.up(1290)]: {
            flexDirection: 'row',
          },
        }}
      >
        <Grid
          size={{ xs: 12, md: 6, lg: 12 }}
          sx={{
            [theme.breakpoints.up(1290)]: {
              flexBasis: `calc(50% - ${theme.spacing(1)})`,
              maxWidth: `calc(50% - ${theme.spacing(1)})`,
            },
          }}
        >
          <Box
            sx={{
              mx: 'auto',
              maxWidth: {
                xs: '100%',
                md: 500,
                lg: 418,
              },
              [theme.breakpoints.up(1290)]: { maxWidth: 500 },
              pt: 2,
            }}
          >
            <RentalFeeCalendar displayRentalPeriods={displayRentalPeriods} flexibleHeight={flexibleHeight} />
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6, lg: 12 }}
          sx={{
            [theme.breakpoints.up(1290)]: {
              flexBasis: `calc(50% - ${theme.spacing(1)})`,
              maxWidth: `calc(50% - ${theme.spacing(1)})`,
            },
          }}
        >
          <Box sx={{ maxWidth: 500, mx: 'auto' }}>
            <RentalFeeDetailsPanelUntyped />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default RentalFeesContentView
